
import React, { useState, useEffect } from "react";
import "./Panel.css";
import Box from "./Box";
import Dropdown from "./Dropdown";
import FooterPanel from "../SectionsPanel/FooterPanel";
import { FaFilter } from "react-icons/fa";

export default function Panel() {
  const [inquiries, setInquiries] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewedIds, setViewedIds] = useState([]);
  const [filters, setFilters] = useState({
    country: "",
    state: "",
    service: "",
    viewStatus: "",
    duration: "",
  });

  // 🔹 Fetch all inquiries initially
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const res = await fetch("https://security-backend-1nbc.onrender.com/api/inquiries");
        if (!res.ok) throw new Error("Failed to fetch inquiries");
        const data = await res.json();
        setInquiries(data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchAll();
  }, []);

  // 🔹 Fetch inquiries based on View Status
  useEffect(() => {
    const fetchViewStatus = async () => {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) return;

        let url = "https://security-backend-1nbc.onrender.com/api/inquiries"; // default fetch all
        if (filters.viewStatus === "viewable") {
          url = "https://security-backend-1nbc.onrender.com/api/views/viewable";
        } else if (filters.viewStatus === "viewed") {
          url = "https://security-backend-1nbc.onrender.com/api/views/viewed";
        }else {
        // Explicitly fetch all for "All" selection
        url = "https://security-backend-1nbc.onrender.com/api/inquiries";
      }

        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch inquiries by view status");

        const data = await res.json();
        setInquiries(data.data || data); // some routes return {data: []}
        if (filters.viewStatus === "viewed") {
          setViewedIds(data.data.map((inq) => inq.id));
        }
      } catch (err) {
        console.error(err);
      }
    };

    // only fetch if viewStatus filter is set
    if (filters.viewStatus) {
      fetchViewStatus();
    }
  }, [filters.viewStatus]);

  // 🔹 Handlers
  const handleDelete = async (id) => {
  // 🔹 Show confirmation alert
  const confirmDelete = window.confirm(
    "Are you sure you want to delete this inquiry? It will be permanently removed from all accounts. This action cannot be undone."
  );
  if (!confirmDelete) return; // user canceled

  try {
    const token = sessionStorage.getItem("token");
    if (!token) return;

    const res = await fetch(`https://security-backend-1nbc.onrender.com/api/inquiries/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) throw new Error("Failed to delete inquiry");

    // 🔹 Remove from local state
    setInquiries((prev) => prev.filter((inq) => inq.id !== id));
    setViewedIds((prev) => prev.filter((vid) => vid !== id));
  } catch (err) {
    console.error("Error deleting inquiry:", err);
  }
};

  const handleSave = async (updatedInquiry) => {
  try {
    const token = sessionStorage.getItem("token");
    if (!token) return;

    const res = await fetch(
      `https://security-backend-1nbc.onrender.com/api/inquiries/${updatedInquiry.id}`,
      {
        method: "PATCH", // or PUT if your API expects it
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedInquiry),
      }
    );

    if (!res.ok) throw new Error("Failed to save inquiry");

    const savedInquiry = await res.json();

    // ✅ Update local state with saved inquiry
    setInquiries((prev) =>
      prev.map((inq) => (inq.id === savedInquiry.id ? savedInquiry : inq))
    );
  } catch (err) {
    console.error("Error saving inquiry:", err);
  }
};

  // 🔹 Apply local filters (search, country, state, service, duration)
  const filteredInquiries = inquiries.filter((inq) => {
    const fullName = (inq.first_name + " " + inq.last_name).toLowerCase();
    const matchesSearch = fullName.includes(searchQuery.toLowerCase());
    const matchesCountry = filters.country ? inq.country === filters.country : true;
    const matchesState = filters.state ? inq.state === filters.state : true;
    const matchesService = filters.service ? inq.service === filters.service : true;
    const matchesDuration = filters.duration ? inq.duration === filters.duration : true;
    return matchesSearch && matchesCountry && matchesState && matchesService && matchesDuration;
  });

  if (filters.sortDate === "asc") {
  filteredInquiries.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
} else if (filters.sortDate === "desc") {
  filteredInquiries.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
}

  return (
    <div className="ci-panel-container">
      {/* Search Bar */}
      <div className="ci-search-bar">
        <input
          type="text"
          placeholder="Search inquiries by name, company, or service..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="ci-panel-body">
        {/* Filters Sidebar */}
        <div className="ci-filters">
          <h3 className="ci-filters-title">
            <FaFilter className="ci-filter-icon" /> Filters
          </h3>

          <Dropdown
            label="Country"
            value={filters.country}
            options={[
              { value: "", label: "All" },
              { value: "United States", label: "United States" },
              { value: "Canada", label: "Canada" },
              { value: "Mexico", label: "Mexico" },
            ]}
            onChange={(val) => setFilters({ ...filters, country: val })}
          />

          <Dropdown
  label="Sort by Date"
  value={filters.sortDate || ""}
  options={[
    { value: "", label: "None" },
    { value: "asc", label: "Oldest First" },
    { value: "desc", label: "Newest First" },
  ]}
  onChange={(val) => setFilters({ ...filters, sortDate: val })}
/>

          <Dropdown
            label="State"
            value={filters.state}
            options={[
              { value: "", label: "All" },
              { value: "California", label: "California" },
              { value: "Quebec", label: "Quebec" },
            ]}
            onChange={(val) => setFilters({ ...filters, state: val })}
          />

          <Dropdown
            label="Service"
            value={filters.service}
            options={[
              { value: "", label: "All" },
              { value: "On-site guards", label: "On-site guards" },
              { value: "Mobile patrol", label: "Mobile patrol" },
              { value: "Event security", label: "Event security" },
              { value: "CCTV & monitoring", label: "CCTV & monitoring" },
              { value: "Risk assessment", label: "Risk assessment" },
            ]}
            onChange={(val) => setFilters({ ...filters, service: val })}
          />

          <Dropdown
            label="Duration"
            value={filters.duration}
            options={[
              { value: "", label: "All" },
              { value: "One day", label: "One day" },
              { value: "1–3 days", label: "1–3 days" },
              { value: "1–4 weeks", label: "1–4 weeks" },
              { value: "Ongoing", label: "Ongoing" },
            ]}
            onChange={(val) => setFilters({ ...filters, duration: val })}
          />

          <Dropdown
            label="View Status"
            value={filters.viewStatus}
            options={[
              { value: "all", label: "All" },
              { value: "viewable", label: "View" },
              { value: "viewed", label: "Viewed" },
            ]}
            onChange={(val) => setFilters({ ...filters, viewStatus: val })}
          />
        </div>

        {/* Results Section */}
        <div className="ci-results">
          <h3>{filteredInquiries.length} Results</h3>
          {filteredInquiries.map((inq) => (
            <Box
              key={inq.id}
              inquiry={inq}
              onDelete={handleDelete}
              onSave={handleSave}
            />
          ))}
        </div>
      </div>

      <FooterPanel />
    </div>
  );
}
